import { ContentDownload, ContentEdit, MenuClose } from '@fs/zion-icon/dist/cjs/icons'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Select,
  TextField,
  useOverlay,
  Paragraph,
} from '@fs/zion-ui'
import Row from '@fs/zion-ui/dist/cjs/row'
import { getUser } from '@fs/zion-user'
import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { putCtr } from '../../../../services/CtrService'
import { deepCopy } from '../../../../util/Utils'
import { CtrContext } from '../../CtrContext'
import { getIdElement, getPidFromIdentifier, setIdInIdentifiers } from '../../../../util/StringUtil'
import { getCisIdAndContributorIdPair } from '../../../../services/userService'
import { hasDuplicatePersonIds } from '../../../../util/TemplePlatformUtil'

export default function CtrSummaryEditButton({
  ogTemplePlatform,
  editableTemplePlatform,
  setEditableTemplePlatform,
  ogCtrExtendedDetail,
  editableCtrExtendedDetail,
  setEditableCtrExtendedDetail,
  isEditing,
  setEditing,
  verifiedPersons,
  setVerifiedPersons,
  verifiedMembers,
  setVerifiedMembers,
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { fetchCurrentCtr } = useContext(CtrContext)
  const saveChangesDialogOpener = useOverlay()

  const saveChanges = async (reasonText, reasonType) => {
    saveChangesDialogOpener.close()
    try {
      const user = await getUser()
      const idPair = await getCisIdAndContributorIdPair(user.cisId, 'cisId')
      const ctrEditRequest = {
        templePlatform: null,
        ctrExtendedDetail: null,
        reasonType,
        reasonText,
        sourceWhoUsysId: `fs-ft.u.${idPair.data.contributorId}`,
        needsAdministrativeReasoning: ogCtrExtendedDetail.hidden !== editableCtrExtendedDetail.hidden,
      }
      const editedTemplePlatformFinal = { ...editableTemplePlatform }
      const editedCtrExtendedDetailFinal = { ...editableCtrExtendedDetail }
      if (
        !editedTemplePlatformFinal.ctrs[0].performedDate ||
        (!editedTemplePlatformFinal.ctrs[0].performedDate.formal &&
          !editedTemplePlatformFinal.ctrs[0].performedDate.original)
      ) {
        editedTemplePlatformFinal.ctrs[0].performedDate = null
      }
      editedTemplePlatformFinal.ctrs[0].templeRef && delete editedTemplePlatformFinal.ctrs[0].templeRef.url
      ctrEditRequest.templePlatform = editedTemplePlatformFinal
      ctrEditRequest.ctrExtendedDetail = editedCtrExtendedDetailFinal
      try {
        await putCtr(ctrEditRequest)
        fetchCurrentCtr()
      } catch (err) {
        let ctrError = err.message
        if (err.response?.data?.notifications?.length > 0) {
          ctrError += ` - ${err.response.data.notifications.map((n) => n.msg).join('; ')}`
        }
        error(
          t('ctr.errors.errorEditingCTR', `Error editing CTR: {message}`, {
            message: ctrError || '',
          }),
          err
        )
      }
    } catch (err) {
      error(
        t('ctr.errors.errorGettingContributorId', {
          defaultValue: `Error getting contributor id for CTR edit: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  const disableSaveBtn = () => {
    if (hasDuplicatePersonIds(editableTemplePlatform.persons)) {
      return true
    }
    if (Object.values(verifiedPersons).some((verifiedPerson) => !verifiedPerson)) {
      return true
    }
    if (Object.values(verifiedMembers).some((verifiedMember) => !verifiedMember)) {
      return true
    }
    if (JSON.stringify(ogTemplePlatform) !== JSON.stringify(editableTemplePlatform)) {
      const editableTemplePlatformCopy = deepCopy(editableTemplePlatform)
      editableTemplePlatformCopy.persons.forEach((person) => {
        if (person.id.includes('qr.')) {
          const pid = getPidFromIdentifier(person.identifiers)
          if (pid) {
            setIdInIdentifiers(
              person.identifiers,
              `p.${getIdElement(pid)}`,
              'http://api.familysearch.org/temple/USYS_ID'
            )
          }
        }
      })
      if (JSON.stringify(ogTemplePlatform) !== JSON.stringify(editableTemplePlatformCopy)) {
        return false
      }
    }
    if (JSON.stringify(ogCtrExtendedDetail) !== JSON.stringify(editableCtrExtendedDetail)) {
      return false
    }
    return true
  }

  const reset = () => {
    setEditing(false)
    setEditableTemplePlatform(deepCopy(ogTemplePlatform))
    setEditableCtrExtendedDetail(deepCopy(ogCtrExtendedDetail))
    setVerifiedPersons({})
    setVerifiedMembers({})
  }

  return (
    <>
      {isEditing ? (
        <Row gutters="nano">
          <Button
            Icon={ContentDownload}
            disabled={disableSaveBtn()}
            emphasis="high"
            onClick={saveChangesDialogOpener.handleClick}
          >
            {t('action.save', 'Save')}
          </Button>
          <Button Icon={MenuClose} emphasis="medium" onClick={reset}>
            {t('action.cancel', 'Cancel')}
          </Button>
          <CtrSummaryEditDialog overlay={saveChangesDialogOpener} saveChanges={saveChanges} />
        </Row>
      ) : (
        <Row gutters="nano">
          <Button emphasis="medium" Icon={ContentEdit} onClick={() => setEditing(true)}>
            {t('action.edit', 'Edit')}
          </Button>
        </Row>
      )}
    </>
  )
}

function CtrSummaryEditDialog({ overlay, saveChanges }) {
  const [reasonType, setReasonType] = useState('OTHER')
  const [reasonText, setReasonText] = useState('')
  const { t } = useTranslation()
  const focusElementRef = useRef()

  return (
    <DialogOverlay focusRef={focusElementRef} title={t('ctr.editCtrTitle', 'Edit CTR')} {...overlay}>
      <Paragraph size="sm">{t('ctr.editMessage', 'Please fill out the following information')}</Paragraph>
      <Select
        fullwidth="true"
        label={t('ctr.editType', 'Edit Type')}
        value={reasonType}
        onChange={(e) => setReasonType(e.target.value)}
        options={[
          {
            value: 'BIC_OVERRIDE_BECAUSE_NOT_BIC',
            label: t('ctr.editReasonType.BIC_OVERRIDE_BECAUSE_NOT_BIC', 'Override Because not BIC'),
          },
          {
            value: 'BIC_OVERRIDE_BY_FIRST_PRESIDENCY_AUTHORIZATION',
            label: t(
              'ctr.editReasonType.BIC_OVERRIDE_BY_FIRST_PRESIDENCY_AUTHORIZATION',
              'BIC Override by First Presidency Authorization'
            ),
          },
          {
            value: 'BIC_REVERT_BACK_TO_BIC',
            label: t('ctr.editReasonType.BIC_REVERT_BACK_TO_BIC', 'Revert Back to BIC'),
          },
          {
            value: 'CHANGE_CTR_PERSON_TO_MATCH_OWS_PERSON',
            label: t(
              'ctr.editReasonType.CHANGE_CTR_PERSON_TO_MATCH_OWS_PERSON',
              'Change CTR Person to Match OWS Person'
            ),
          },
          {
            value: 'CMIS_TEMPLE_ORDINANCE',
            label: t('ctr.editReasonType.CMIS_TEMPLE_ORDINANCE', 'CMIS Temple Ordinance'),
          },
          { value: 'CHILD', label: t('ctr.editReasonType.CHILD', 'Child') },
          {
            value: 'DESCRIPTION_FOR_A_PROPER_NAME',
            label: t('ctr.editReasonType.DESCRIPTION_FOR_A_PROPER_NAME', 'Description for a Proper Name'),
          },
          {
            value: 'FIRST_PRESIDENCY_DECISION',
            label: t('ctr.editReasonType.FIRST_PRESIDENCY_DECISION', 'First Presidency Decision'),
          },
          {
            value: 'INFERRED_BY_OTHER_ORDINANCE',
            label: t('ctr.editReasonType.INFERRED_BY_OTHER_ORDINANCE', 'Inferred by Other Ordinance'),
          },
          {
            value: 'LEGACY_SYSTEM_CHANGES_APPLIED',
            label: t('ctr.editReasonType.LEGACY_SYSTEM_CHANGES_APPLIED', 'Legacy System Changes Applied'),
          },
          {
            value: 'LEGACY_SYSTEM_VALIDATION_UNCERTAIN',
            label: t('ctr.editReasonType.LEGACY_SYSTEM_VALIDATION_UNCERTAIN', 'Legacy System Validation Uncertain'),
          },
          { value: 'ORS_MODIFIED_LOR', label: t('ctr.editReasonType.ORS_MODIFIED_LOR', 'ORS Modified LOR') },
          {
            value: 'ORIG_PERSON_TO_CURRENT_OWS_PERSON',
            label: t('ctr.editReasonType.ORIG_PERSON_TO_CURRENT_OWS_PERSON', 'Original Person to Current OWS Person'),
          },
          { value: 'OTHER', label: t('ctr.editReasonType.OTHER', 'Other') },
          {
            value: 'PERFORMED_INCORRECTLY',
            label: t('ctr.editReasonType.PERFORMED_INCORRECTLY', 'Performed Incorrectly'),
          },
          { value: 'PROXY_FOR_LIVING', label: t('ctr.editReasonType.PROXY_FOR_LIVING', 'Proxy for Living') },
          {
            value: 'REPRINTED_ON_NEW_REQUEST',
            label: t('ctr.editReasonType.REPRINTED_ON_NEW_REQUEST', 'Reprinted on new Request'),
          },
          {
            value: 'RERECORD_INVALIDATED_ORDINANCE',
            label: t('ctr.editReasonType.RERECORD_INVALIDATED_ORDINANCE', 'Re-record Invalidated Ordinance'),
          },
          {
            value: 'RECORDED_WRONG_PERFORM_DATE_OR_TEMPLE_CODE',
            label: t(
              'ctr.editReasonType.RECORDED_WRONG_PERFORM_DATE_OR_TEMPLE_CODE',
              'Recorded Wrong Perform Date or Temple Code'
            ),
          },
          {
            value: 'SCANNED_ORDINANCE_NOT_PERFORMED',
            label: t('ctr.editReasonType.SCANNED_ORDINANCE_NOT_PERFORMED', 'Scanned Ordinance not Performed'),
          },
          { value: 'STILLBORN', label: t('ctr.editReasonType.STILLBORN', 'Stillborn') },
          { value: 'UNDER_AGE', label: t('ctr.editReasonType.UNDER_AGE', 'Under Age') },
          {
            value: 'UNWORTHY_PROXY_OR_TEMPLE_WORKER',
            label: t('ctr.editReasonType.UNWORTHY_PROXY_OR_TEMPLE_WORKER', 'Unworthy Proxy or Temple Worker'),
          },
          { value: 'WRONG_GENDER', label: t('ctr.editReasonType.WRONG_GENDER', 'Wrong Gender') },
        ]}
      />
      <TextField
        ref={focusElementRef}
        title={t('reason', 'Reason')}
        value={reasonText}
        label={t('reason', 'Reason')}
        placeholder={
          reasonType === 'OTHER'
            ? t('ctr.dialog.editCtrTextFieldPlaceholder', 'Please provide a reason for your edit')
            : ''
        }
        onChange={(e) => setReasonText(e.target.value)}
        fullwidth="true"
      />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={overlay.close}>{t('action.cancel', 'Cancel')}</Button>
          <Button
            disabled={reasonType === 'OTHER' && reasonText === ''}
            onClick={() => saveChanges(reasonText, reasonType)}
            emphasis="high"
          >
            {t('action.submit', 'Submit')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
